'use client';

import { useMemo } from 'react';
import {
  Field,
  Select,
  createSelectListCollection,
} from '@terra/ui-design-system';
import { FEEDBACK_FAILURE_AREA_ORDER } from '@terra/shared-contracts-signal-feedback';
import type { FeedbackFailureArea } from '@terra/shared-contracts-signal-feedback';
import { useTranslations } from 'next-intl';

type SignalFeedbackFailureAreaSelectorProps = {
  value?: FeedbackFailureArea;
  disabled?: boolean;
  onValueChange: (value: FeedbackFailureArea | undefined) => void;
};

export function SignalFeedbackFailureAreaSelector({
  value,
  disabled,
  onValueChange,
}: SignalFeedbackFailureAreaSelectorProps) {
  const t = useTranslations('signals.signalStatusSelector');
  const collection = useMemo(
    () =>
      createSelectListCollection({
        items: FEEDBACK_FAILURE_AREA_ORDER.map((area) => ({
          value: area,
          label: t(`failureAreaOptions.${area}`),
        })),
      }),
    [t],
  );

  return (
    <Field.Root width="full">
      <Field.Label>{t('failureArea')}</Field.Label>
      <Select.Root
        size="sm"
        collection={collection}
        value={value ? [value] : []}
        disabled={disabled}
        onValueChange={(details) => {
          const nextValue = details.value[0];

          onValueChange(
            nextValue ? (nextValue as FeedbackFailureArea) : undefined,
          );
        }}
        positioning={{ strategy: 'fixed' }}
      >
        <Select.Control>
          <Select.Trigger aria-label={t('failureArea')}>
            <Select.ValueText placeholder={t('failureAreaPlaceholder')} />
          </Select.Trigger>
        </Select.Control>
        <Select.Positioner>
          <Select.Content maxHeight="240px" overflowY="auto">
            {collection.items.map((item) => (
              <Select.Item key={item.value} item={item}>
                <Select.ItemText>{item.label}</Select.ItemText>
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Select.Root>
    </Field.Root>
  );
}
